import { useNavigate } from 'react-router-dom'

import { Button, IllustrationType } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface SoftDeleteFormValues {
  deleteInput: string | undefined
}

export interface SoftDeleteProjectModalProps {
  onSubmit: () => Promise<UnhandledResult<Result<unknown>>>
  loading: boolean
  title: string
  description: React.ReactNode
  iconName: IllustrationType
  inputLabel?: string
  submitButtonLabel: string
}

const SoftDeleteProjectModal = ({
  onSubmit,
  loading,
  title,
  description,
  iconName,
  inputLabel,
  submitButtonLabel,
}: SoftDeleteProjectModalProps) => {
  const navigate = useNavigate()

  const onClose = () => {
    navigate(-1)
  }

  const form = useForm<SoftDeleteFormValues>({
    defaultValues: {
      deleteInput: undefined,
    },
    onSubmit: async (_data, { setErrors }) => {
      const result = await onSubmit()

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.removed_successfully'),
        })

        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      illustration={iconName}
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={() => {
        form.dirty ? form.handleSubmit() : undefined
      }}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            submit
            disabled={
              loading ||
              form.values.deleteInput !== i18n.t('project_management.modals.delete_project.delete')
            }
          >
            {submitButtonLabel}
          </Button>
        </>
      }
    >
      <TextField
        mandatory
        label={inputLabel}
        value={form.values.deleteInput}
        onChange={(value) => form.setValues({ ...form.values, deleteInput: value })}
      />
    </Modal>
  )
}

export default observer(SoftDeleteProjectModal)
